import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { trackEvent } from "@/lib/api";
import { trackGoogleEvent } from "@/lib/googleTracking";
import { decorateCheckoutUrl, getAttribution } from "@/lib/attribution";
import { PRODUCT } from "@/data/content";
import { useStudyAuth } from "@/context/StudyAuth";

const VARIANTS = {
  primary:
    "bg-[var(--ibp-gold-500)] hover:bg-[var(--ibp-primary-hover)] text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.22)] focus-visible:ring-offset-[var(--ibp-navy-950)]",
  primaryLight:
    "bg-[var(--ibp-gold-500)] hover:bg-[var(--ibp-primary-hover)] text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] focus-visible:ring-offset-white",
};

const SIZES = {
  default: "min-h-11 px-5 py-3 text-sm",
  lg: "min-h-12 px-6 py-3.5 text-base",
  sm: "min-h-0 px-4 py-2 text-sm",
};

export const BuyButton = ({
  label,
  testId,
  variant = "primary",
  size = "default",
  className,
  fullWidth = false,
  onClick,
}) => {
  const { data: config } = useSiteConfig();
  const { email, license, isAuthed } = useStudyAuth();
  const price = PRODUCT.price || "79";
  const text = label || `Get full access — $${price}`;

  const handleClick = () => {
    if (typeof onClick === "function") onClick();
    const attribution = getAttribution();
    trackEvent("checkout_click", {
      configured: Boolean(config?.configured),
      has_attribution: Object.keys(attribution).length > 0,
    });
    trackGoogleEvent("begin_checkout", {
      currency: "USD",
      value: Number(price || "79"),
      has_attribution: Object.keys(attribution).length > 0,
      items: [{ item_name: "Illinois Broker Prep Online Study Platform" }],
    });
    if (config?.configured && config?.shopify_checkout_url) {
      window.open(
        decorateCheckoutUrl(config.shopify_checkout_url, {
          email: isAuthed ? email : "",
          accountId: isAuthed ? license?.account_id : "",
        }),
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      toast("Secure checkout opening soon", {
        description:
          "Our secure checkout is being connected. Please check back shortly to complete your purchase.",
        icon: null,
      });
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className
      )}
    >
      <Lock className="h-4 w-4 opacity-70" aria-hidden="true" />
      <span>{text}</span>
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};

export default BuyButton;
